import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Stack, Alert, TextField, FormControlLabel, Switch, IconButton, CircularProgress, Autocomplete } from '@mui/material';
import { Search, Delete, ArrowUpward, ArrowDownward, Download, Place } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import TopNav from './TopNav.jsx';
import Footer from './Footer.jsx';
import GlassCard from './GlassCard.jsx';
import CategoryChips from './CategoryChips.jsx';
import ActionButton from './ActionButton.jsx';
import ShareControl from './ShareControl.jsx';
import SaveMapButton from './SaveMapButton.jsx';
import QuickOrderStrip from './QuickOrderStrip.jsx';
import ColorPickerControl from './ColorPickerControl.jsx';
import RoadTripRender from './roadtrip/RoadTripRender.jsx';
import { getMapForReopen } from '../utils/mapsRepository';
import { makeThumbnail } from '../utils/mapThumbnail';
import { useAuth } from '../auth/AuthContext';
import { hasGoogleKey, fetchPlaceSuggestions, resolveSuggestion, newSessionToken, routeThroughStops, straightLineFallback, MAX_STOPS, STATIC_MAPTYPES, roadTripPreviewUrl } from '../utils/places';
import { formatDistance } from '../utils/gpx';
import useDocumentTitle from '../utils/useDocumentTitle';
import { storage } from '../firebaseApp';
import { orderProduct } from '../actions/shapeActions';
import { trackDownload, trackAddToCart } from '../utils/analytics';
import { trackFbDownload, trackFbAddToCart } from '../utils/facebookPixel';
import { trackPinDownload, trackPinAddToCart } from '../utils/pinterestTag';
import { PALETTE } from '../theme';

const TRACK_LABEL = 'Road Trip';

const STYLE_OPTIONS = STATIC_MAPTYPES.map((s) => ({ key: s.id, text: s.label, value: s.id }));
const UNIT_OPTIONS = [{ key: 'metric', text: 'km', value: 'metric' }, { key: 'imperial', text: 'mi', value: 'imperial' }];
const SHOW_TOGGLES = [{ key: 'title', label: 'Title' }, { key: 'stats', label: 'Stats' }];
const STAT_FIELD_DEFS = [{ key: 'stops', label: 'Stops' }, { key: 'distance', label: 'Distance' }];
const DEFAULT_STYLE = 'roadmap';

const SectionLabel = ({ children }) => (
    <Typography sx={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', color: PALETTE.textMuted, mb: 1 }}>{children}</Typography>
);

const rgbStr = (c) => `rgb(${c.r}, ${c.g}, ${c.b})`;
const hex2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
const rgbToStaticHex = (c) => `0x${hex2(c.r)}${hex2(c.g)}${hex2(c.b)}`;

/**
 * Road Trips — search stops (Google Places), rename them, reorder them, and we
 * draw the drive between them on the Google basemap (Static Maps API). Same
 * chrome + components as the rest of the site (TopNav / Footer / GlassCard /
 * CategoryChips / QuickOrderStrip). Google supplies search + routing + the
 * basemap; export/order go through the roadTripStaticMap Cloud Function.
 */
const RoadTripPage = () => {
    useDocumentTitle('Road Trips — Map Shaper');
    const dispatch = useDispatch();
    const { authReady, isSignedIn } = useAuth();
    const [stops, setStops] = useState([]);
    const [title, setTitle] = useState('My Road Trip');
    const [units, setUnits] = useState('metric');
    const [styleId, setStyleId] = useState(DEFAULT_STYLE);
    const [show, setShow] = useState({ title: true, stats: true });
    const [statFields, setStatFields] = useState({ stops: true, distance: true });
    const [accentColor, setAccentColor] = useState({ r: 232, g: 99, b: 45, a: 255 });
    const [route, setRoute] = useState(null);
    const [routing, setRouting] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [openMap, setOpenMap] = useState(null); // saved map currently open (?open=<id>)

    // Place search (Places API "New", rendered in our own MUI Autocomplete).
    const [searchInput, setSearchInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [searching, setSearching] = useState(false);

    const renderRef = useRef(null);
    const idRef = useRef(0);
    const routeReqRef = useRef(0);
    const sessionTokenRef = useRef(null);

    const accent = rgbStr(accentColor);        // UI badges
    const colorHex = rgbToStaticHex(accentColor); // Static Maps route/marker color

    // ── Place search (Google Places Autocomplete) ──────────────────────────
    const addStop = useCallback((place) => {
        setStops((prev) => {
            if (prev.length >= MAX_STOPS) { setError(`A road trip can have up to ${MAX_STOPS} stops.`); return prev; }
            idRef.current += 1;
            return [...prev, { id: idRef.current, ...place }];
        });
    }, []);

    // Debounced type-ahead against the Places API. One session token spans the
    // keystrokes of a single search and retires when a result is picked.
    useEffect(() => {
        if (!hasGoogleKey) return undefined;
        const q = searchInput.trim();
        if (q.length < 2) { setSuggestions([]); setSearching(false); return undefined; }
        let active = true;
        setSearching(true);
        const t = setTimeout(async () => {
            try {
                if (!sessionTokenRef.current) sessionTokenRef.current = await newSessionToken();
                const preds = await fetchPlaceSuggestions(q, sessionTokenRef.current);
                if (active) setSuggestions(preds);
            } catch {
                if (active) { setSuggestions([]); setError('Place search could not load. Check the Google Maps key.'); }
            } finally {
                if (active) setSearching(false);
            }
        }, 300);
        return () => { active = false; clearTimeout(t); };
    }, [searchInput]);

    // Resolve the picked suggestion into a stop (this details fetch also closes
    // the billing session the keystroke suggestions belonged to).
    const handlePick = useCallback(async (opt) => {
        if (!opt || typeof opt === 'string') return;
        try {
            const place = await resolveSuggestion(opt);
            addStop(place);
        } catch {
            setError('Could not add that place. Please try again.');
        } finally {
            sessionTokenRef.current = null;
            setSearchInput('');
            setSuggestions([]);
        }
    }, [addStop]);

    const renameStop = (id, name) => setStops((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
    const removeStop = (id) => setStops((prev) => prev.filter((s) => s.id !== id));
    const moveStop = (idx, dir) => setStops((prev) => {
        const next = [...prev];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return prev;
        [next[idx], next[j]] = [next[j], next[idx]];
        return next;
    });

    // ── Route geometry (recompute on add / remove / reorder, NOT rename) ───
    const geomKey = useMemo(() => stops.map((s) => `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`).join('|'), [stops]);
    const stopsRef = useRef(stops);
    stopsRef.current = stops;

    useEffect(() => {
        const current = stopsRef.current;
        if (current.length < 2) { setRoute(current.length === 1 ? straightLineFallback(current) : null); setRouting(false); return; }
        const reqId = ++routeReqRef.current;
        setRouting(true);
        const t = setTimeout(async () => {
            try {
                const r = await routeThroughStops(current);
                if (routeReqRef.current === reqId) setRoute(r);
            } catch {
                if (routeReqRef.current === reqId) setRoute(straightLineFallback(current));
            } finally {
                if (routeReqRef.current === reqId) setRouting(false);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [geomKey]);

    // Live product-mockup thumbnail = the raw Static Maps preview URL (browser
    // key). No Cloud Function needed, so the product preview works immediately.
    // The full print/order image (with title + stats baked in) still comes from
    // capture() → the Cloud Function. The small mockup is an "approximate
    // preview" (no overlays), which is exactly what the strip already promises.
    const designUrl = useMemo(
        () => roadTripPreviewUrl({ stops, polyline: route ? route.encodedPolyline : null, maptype: styleId, color: colorHex }),
        [stops, route, styleId, colorHex]
    );

    const captureBlob = useCallback(() => renderRef.current.capture(), []);

    const prepareImage = useCallback((onProgress) => new Promise(async (resolve, reject) => {
        try {
            const blob = await captureBlob();
            const id = Math.random().toString(36).substring(2) + Date.now().toString(36) + '.png';
            const task = uploadBytesResumable(ref(storage, 'images/' + id), blob);
            task.on('state_changed',
                (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
                reject,
                async () => { try { resolve(await getDownloadURL(task.snapshot.ref)); } catch (e) { reject(e); } });
        } catch (e) { reject(e); }
    }), [captureBlob]);

    const onOrder = useCallback(({ product, label, imageUrl }) => {
        const count = stops.length;
        trackAddToCart({ product: product.analyticsKey, kind: 'roadtrip', mapType: 'roadtrip', visitedCount: count, totalCount: count });
        trackFbAddToCart(product.name, TRACK_LABEL);
        trackPinAddToCart(product.name, TRACK_LABEL);
        dispatch(orderProduct(imageUrl, product, label));
    }, [dispatch, stops.length]);

    const onDownload = async () => {
        try {
            setBusy(true);
            trackDownload({ kind: 'roadtrip', mapType: 'roadtrip', visitedCount: stops.length, totalCount: stops.length });
            trackFbDownload(TRACK_LABEL);
            trackPinDownload(TRACK_LABEL);
            const blob = await captureBlob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(title || 'road-trip').replace(/[^\w-]+/g, '-').toLowerCase()}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            setError('Could not export the map. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    // ── Saved-map adapter ──────────────────────────────────────────────────
    // Route geometry is NOT saved — it re-fetches from the stops on reopen.
    const getSnapshot = useCallback(async () => {
        let thumbnailBlob = null;
        try {
            thumbnailBlob = await makeThumbnail(await captureBlob());
        } catch {
            /* thumbnail is best-effort (capture needs the Cloud Function) */
        }
        return {
            type: 'roadtrip',
            defaultName: title || 'Road trip',
            breadcrumb: ['Road Trip'],
            settings: { stops, title, units, styleId, show, statFields, accentColor },
            thumbnailBlob,
        };
    }, [captureBlob, stops, title, units, styleId, show, statFields, accentColor]);

    // Reopen a saved road trip (My Maps → ?open=<id>). Waits for Firebase to
    // restore the session first, so a cold load / refresh of the URL works too.
    const reopenedRef = useRef(false);
    useEffect(() => {
        if (reopenedRef.current || !authReady || !isSignedIn) return;
        const id = new URLSearchParams(window.location.search).get('open');
        if (!id) return;
        reopenedRef.current = true;
        (async () => {
            try {
                const rec = await getMapForReopen(id);
                if (rec.type !== 'roadtrip') return;
                const s = rec.settings || {};
                if (Array.isArray(s.stops)) {
                    setStops(s.stops);
                    idRef.current = s.stops.reduce((m, x) => Math.max(m, x.id || 0), 0);
                }
                if (s.title != null) setTitle(s.title);
                if (s.units) setUnits(s.units);
                if (s.styleId) setStyleId(s.styleId);
                if (s.show) setShow(s.show);
                if (s.statFields) setStatFields(s.statFields);
                if (s.accentColor) setAccentColor(s.accentColor);
                setOpenMap({ id: rec.id, name: rec.name });
            } catch (e) {
                setError(e.message || 'Could not load that saved map.');
            }
        })();
    }, [authReady, isSignedIn]);

    const toggleShow = (key) => setShow((p) => ({ ...p, [key]: !p[key] }));
    const toggleStat = (key) => setStatFields((p) => ({ ...p, [key]: !p[key] }));

    const distanceM = route && route.totalDistanceM != null ? route.totalDistanceM : null;
    const caption = distanceM != null ? `${title} · ${formatDistance(distanceM, units)}` : title;
    const hasMap = stops.length > 0;

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: PALETTE.page }}>
            <TopNav />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
                    <Typography component="h1" sx={{ fontSize: { xs: '1.9rem', sm: '2.5rem' }, fontWeight: 800, color: PALETTE.text, letterSpacing: '-0.02em', lineHeight: 1.1, mb: 1 }}>Road Trips</Typography>
                    <Typography sx={{ color: PALETTE.textBody, fontSize: { xs: '0.95rem', sm: '1.05rem' }, maxWidth: 560, mx: 'auto', lineHeight: 1.5 }}>
                        Search the places on your trip, drop them in order, and we'll draw the drive between them — download the image, or order it on a cup, t-shirt and more.
                    </Typography>
                </Box>

                {!hasGoogleKey && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Place search isn't configured yet. Add a Google Maps key (<code>REACT_APP_GOOGLE_MAPS_KEY</code>) to enable searching for stops.
                    </Alert>
                )}

                {/* Stop builder */}
                <GlassCard variant="frame" sx={{ mb: 3, p: { xs: 2, sm: 2.5 } }}>
                    <Stack spacing={2.5}>
                        <Box>
                            <SectionLabel>ADD A STOP</SectionLabel>
                            <Autocomplete
                                fullWidth
                                size="small"
                                disabled={!hasGoogleKey || stops.length >= MAX_STOPS}
                                options={suggestions}
                                filterOptions={(x) => x}   // Google already ranked/filtered; don't re-filter client-side
                                getOptionLabel={(o) => (typeof o === 'string' ? o : (o.label || ''))}
                                isOptionEqualToValue={(o, v) => o.placeId === v.placeId}
                                value={null}
                                inputValue={searchInput}
                                onInputChange={(_e, v, reason) => {
                                    if (reason === 'input') setSearchInput(v);
                                    else if (reason === 'clear') setSearchInput('');
                                }}
                                onChange={(_e, opt) => handlePick(opt)}
                                blurOnSelect
                                clearOnBlur
                                loading={searching}
                                loadingText="Searching…"
                                noOptionsText={searchInput.trim().length < 2 ? 'Type to search…' : 'No matches'}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search a city, landmark or address…"
                                        helperText={stops.length >= MAX_STOPS ? `Maximum ${MAX_STOPS} stops reached.` : 'Pick a result to add it as your next stop.'}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: <Search sx={{ color: PALETTE.textMuted, mr: 1 }} fontSize="small" />,
                                        }}
                                    />
                                )}
                                renderOption={(props, opt) => {
                                    const { key, ...rest } = props;
                                    return (
                                        <Box component="li" key={key} {...rest} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Typography sx={{ fontSize: '0.9rem', color: PALETTE.text }}>{opt.primary || opt.label}</Typography>
                                            {opt.secondary && (
                                                <Typography sx={{ fontSize: '0.78rem', color: PALETTE.textMuted }}>{opt.secondary}</Typography>
                                            )}
                                        </Box>
                                    );
                                }}
                            />
                        </Box>

                        {stops.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 2, color: PALETTE.textMuted }}>
                                <Place sx={{ fontSize: 30, color: PALETTE.textVeryFaint, mb: 0.5 }} />
                                <Typography sx={{ fontSize: 14 }}>No stops yet — search above to add your first one.</Typography>
                            </Box>
                        ) : (
                            <Box>
                                <SectionLabel>STOPS (IN ORDER)</SectionLabel>
                                <Stack spacing={1}>
                                    {stops.map((s, i) => (
                                        <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 26, height: 26, flexShrink: 0, borderRadius: '50%', background: accent, color: '#fff', fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</Box>
                                            <TextField value={s.name} onChange={(e) => renameStop(s.id, e.target.value)} size="small" fullWidth inputProps={{ maxLength: 40 }} />
                                            <IconButton size="small" onClick={() => moveStop(i, -1)} disabled={i === 0} aria-label="Move up"><ArrowUpward fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => moveStop(i, 1)} disabled={i === stops.length - 1} aria-label="Move down"><ArrowDownward fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => removeStop(s.id)} aria-label="Remove stop"><Delete fontSize="small" /></IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                                {routing && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, color: PALETTE.textMuted }}>
                                        <CircularProgress size={14} thickness={5} />
                                        <Typography sx={{ fontSize: 13 }}>Finding the best drive…</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Stack>
                </GlassCard>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

                {hasMap && (
                    <>
                        <GlassCard variant="frame" sx={{ mb: 3, p: { xs: 2, sm: 2.5 } }}>
                            <Stack spacing={2.5}>
                                <TextField label="Map title" value={title} onChange={(e) => setTitle(e.target.value)} size="small" fullWidth inputProps={{ maxLength: 48 }} helperText="Shown centered on the map." />

                                <Box>
                                    <SectionLabel>MAP STYLE</SectionLabel>
                                    <CategoryChips value={styleId} onChange={setStyleId} options={STYLE_OPTIONS} sx={{ justifyContent: 'flex-start' }} />
                                </Box>

                                <Box>
                                    <SectionLabel>UNITS</SectionLabel>
                                    <CategoryChips value={units} onChange={setUnits} options={UNIT_OPTIONS} sx={{ justifyContent: 'flex-start' }} />
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                    <Box>
                                        <SectionLabel>SHOW (on the map)</SectionLabel>
                                        <Stack direction="row" flexWrap="wrap" sx={{ gap: { xs: 0, sm: 1 } }}>
                                            {SHOW_TOGGLES.map((t) => (
                                                <FormControlLabel key={t.key} control={<Switch size="small" checked={!!show[t.key]} onChange={() => toggleShow(t.key)} />} label={<Typography sx={{ fontSize: 14, color: PALETTE.textBody }}>{t.label}</Typography>} />
                                            ))}
                                        </Stack>
                                    </Box>
                                    <Box>
                                        <SectionLabel>ROUTE COLOR</SectionLabel>
                                        <ColorPickerControl
                                            color={accentColor}
                                            onChange={(channel, value) => setAccentColor((p) => ({ ...p, [channel]: value }))}
                                            onPresetClick={(preset) => setAccentColor(preset)}
                                        />
                                    </Box>
                                </Box>

                                {show.stats && (
                                    <Box>
                                        <SectionLabel>STATS TO SHOW</SectionLabel>
                                        <Stack direction="row" flexWrap="wrap" sx={{ gap: { xs: 0, sm: 1 } }}>
                                            {STAT_FIELD_DEFS.map((f) => (
                                                <FormControlLabel key={f.key} control={<Switch size="small" checked={!!statFields[f.key]} onChange={() => toggleStat(f.key)} />} label={<Typography sx={{ fontSize: 14, color: PALETTE.textBody }}>{f.label}</Typography>} />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </Stack>
                        </GlassCard>

                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <RoadTripRender
                                ref={renderRef}
                                stops={stops}
                                polyline={route ? route.encodedPolyline : null}
                                maptype={styleId}
                                colorHex={colorHex}
                                title={title}
                                units={units}
                                show={show}
                                statFields={statFields}
                                distanceM={route ? route.totalDistanceM : null}
                                source={route ? route.source : 'roads'}
                            />
                        </Box>

                        {/* products + download + share — same components as the other pillars */}
                        <QuickOrderStrip defaultLabel={caption} prepareImage={prepareImage} onOrder={onOrder} designSrc={designUrl} />

                        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1 }, mb: 4 }}>
                            <ActionButton variant="ghost" startIcon={<Download sx={{ fontSize: 18 }} />} onClick={onDownload} disabled={busy} loading={busy} sx={{ width: 'auto', minWidth: 0, px: 1.5 }}>
                                Download
                            </ActionButton>
                            <SaveMapButton
                                getSnapshot={getSnapshot}
                                openMap={openMap}
                                onSaved={setOpenMap}
                                onStatus={(msg, variant) => (variant === 'error' ? setError(msg) : setNotice(msg))}
                                variant="ghost"
                                sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                            />
                            <ShareControl
                                kind="roadtrip"
                                roadtripTitle={title}
                                roadtripStops={stops.length}
                                roadtripDistance={distanceM != null ? formatDistance(distanceM, units) : ''}
                                getBlob={captureBlob}
                                fileName={`${(title || 'road-trip').replace(/[^\w-]+/g, '-').toLowerCase()}.png`}
                                trackId="road_trip"
                                variant="ghost"
                                sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                            />
                        </Box>
                    </>
                )}
            </Box>

            <Footer />
        </Box>
    );
};

export default RoadTripPage;
